/**
 * Calendar maths for the production planner.
 *
 * Every date is a `YYYY-MM-DD` string (what Postgres `date` columns and
 * `<input type="date">` speak). Arithmetic goes through UTC so a DST
 * change never makes a day 23 or 25 hours long and shifts the result.
 */

export type IsoDate = string;

const DAY_MS = 86_400_000;

export function parseIsoDate(value: IsoDate): number {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function toIsoDate(ms: number): IsoDate {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return toIsoDate(parseIsoDate(date) + days * DAY_MS);
}

/** `b − a` in calendar days (positive when `b` is later). */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((parseIsoDate(b) - parseIsoDate(a)) / DAY_MS);
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

export function minDate(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b;
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: IsoDate): number {
  return new Date(parseIsoDate(date)).getUTCDay();
}

/** `YYYY-MM` of a date. */
export function monthKey(date: IsoDate): string {
  return date.slice(0, 7);
}

export function startOfMonth(date: IsoDate): IsoDate {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: IsoDate): IsoDate {
  const [y, m] = date.slice(0, 7).split('-').map(Number);
  return toIsoDate(Date.UTC(y, m, 0));
}

export function addMonths(date: IsoDate, months: number): IsoDate {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return toIsoDate(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, lastDay)));
}

/** Monday-anchored week start, so weekly buckets line up with a work week. */
export function startOfWeek(date: IsoDate): IsoDate {
  const dow = dayOfWeek(date);
  return addDays(date, dow === 0 ? -6 : 1 - dow);
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Which days the floor builds. `workingDays` are weekday numbers
 * (0 = Sunday); `holidays` are dates that are closed regardless.
 */
export class WorkCalendar {
  private readonly weekdays: ReadonlySet<number>;
  private readonly holidays: ReadonlySet<IsoDate>;

  constructor(workingDays: Iterable<number>, holidays: Iterable<IsoDate> = []) {
    const weekdays = new Set(workingDays);
    // An empty working-day set would make every loop below spin forever;
    // fall back to Monday–Friday.
    this.weekdays = weekdays.size ? weekdays : new Set([1, 2, 3, 4, 5]);
    this.holidays = new Set(Array.from(holidays, (h) => h.slice(0, 10)));
  }

  isWorkingDay(date: IsoDate): boolean {
    return this.weekdays.has(dayOfWeek(date)) && !this.holidays.has(date);
  }

  isHoliday(date: IsoDate): boolean {
    return this.holidays.has(date);
  }

  /** `date` itself when it is a working day, else the next one. */
  nextWorkingDay(date: IsoDate): IsoDate {
    let d = date;
    for (let i = 0; i < 400 && !this.isWorkingDay(d); i++) d = addDays(d, 1);
    return d;
  }

  /** `date` itself when it is a working day, else the previous one. */
  previousWorkingDay(date: IsoDate): IsoDate {
    let d = date;
    for (let i = 0; i < 400 && !this.isWorkingDay(d); i++) d = addDays(d, -1);
    return d;
  }

  /** Working days in `[from, to]`, inclusive. */
  workingDaysBetween(from: IsoDate, to: IsoDate): IsoDate[] {
    const out: IsoDate[] = [];
    if (to < from) return out;
    let cursor = parseIsoDate(from);
    const end = parseIsoDate(to);
    while (cursor <= end) {
      const iso = toIsoDate(cursor);
      if (this.isWorkingDay(iso)) out.push(iso);
      cursor += DAY_MS;
    }
    return out;
  }

  countWorkingDays(from: IsoDate, to: IsoDate): number {
    return this.workingDaysBetween(from, to).length;
  }

  /** Every working day in the calendar month containing `date`. */
  workingDaysInMonth(date: IsoDate): IsoDate[] {
    return this.workingDaysBetween(startOfMonth(date), endOfMonth(date));
  }

  /**
   * Working days in `[from, to]` from `from`'s month, i.e. the days of
   * production still ahead when a monthly target is set mid-month.
   */
  remainingWorkingDaysInMonth(from: IsoDate): IsoDate[] {
    return this.workingDaysBetween(from, endOfMonth(from));
  }

  /** The `n`th working day after `date` (`n` = 0 returns `date`'s next working day). */
  addWorkingDays(date: IsoDate, n: number): IsoDate {
    let d = this.nextWorkingDay(date);
    let remaining = n;
    while (remaining > 0) {
      d = this.nextWorkingDay(addDays(d, 1));
      remaining--;
    }
    return d;
  }
}
